import React, { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import {
  TrendingUp,
  Sparkles,
  Flame,
  CheckCircle2,
  ArrowRight,
  List,
  BookOpen,
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  Link2,
  Check,
  ShieldCheck,
  Award,
} from "lucide-react";
import { BlogPost as BlogPostType } from "@/data/blogPosts";
import { useToast } from "@/hooks/use-toast";

interface BlogPostSidebarProps {
  slug: string;
  topRankingArticles: BlogPostType[];
  postTitle: string;
}

export function BlogPostSidebar({
  slug,
  topRankingArticles,
  postTitle,
}: BlogPostSidebarProps) {
  const { toast } = useToast();
  const [auditEmail, setAuditEmail] = useState("");
  const [copied, setCopied] = useState(false);
  const [headings, setHeadings] = useState<
    { id: string; text: string; level: number }[]
  >([]);
  const [activeId, setActiveId] = useState<string>("");

  // Scan prose headings for dynamic Table of Contents
  useEffect(() => {
    const prose = document.querySelector(".prose");
    if (!prose) return;
    const elements = prose.querySelectorAll("h2, h3");
    const extracted: { id: string; text: string; level: number }[] = [];

    elements.forEach((el, index) => {
      let id = el.id;
      if (!id) {
        id = `section-${index}-${el.textContent
          ?.toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .slice(0, 32)}`;
        el.id = id;
      }
      const rawText = el.textContent || "";
      // Filter out CTA titles or empty headings
      if (
        rawText.trim().length > 3 &&
        !rawText.toLowerCase().includes("book a") &&
        !rawText.toLowerCase().includes("ready to")
      ) {
        extracted.push({
          id,
          text: rawText,
          level: el.tagName === "H2" ? 2 : 3,
        });
      }
    });

    setHeadings(extracted.slice(0, 8)); // Top 8 structural headings

    // Observe active heading on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-80px 0px -60% 0px" },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [slug]);

  const handleAuditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!auditEmail || !auditEmail.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid work email address.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Audit Request Received",
      description: "We'll send your custom systems delegation audit shortly.",
    });
    setAuditEmail("");
  };

  const shareUrl = `https://marjohnrobillo.me/blog/${slug}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast({
      title: "Link copied to clipboard",
      description: "Share it with your team or engineering leads.",
    });
    setTimeout(() => setCopied(false), 2400);
  };

  return (
    <aside className="space-y-6 lg:sticky lg:top-24">
      {/* 1. Quick Table of Contents (Architectural Whitepaper Feature) */}
      {headings.length > 2 && (
        <div className="bg-white rounded-2xl border border-[#082E67]/10 p-5 shadow-[0_8px_24px_rgba(8,46,103,0.04)]">
          <div className="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-slate-100">
            <span className="w-6 h-6 rounded-md bg-[#EDF7FF] text-[#0B7CF2] flex items-center justify-center">
              <List className="w-3.5 h-3.5" />
            </span>
            <span className="text-[12px] font-black uppercase tracking-wider text-[#071E61]">
              Table of Contents
            </span>
          </div>
          <nav className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1 text-xs">
            {headings.map((h) => {
              const isActive = activeId === h.id;
              return (
                <a
                  key={h.id}
                  href={`#${h.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(h.id)
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`block py-1 px-2 rounded-md transition-all truncate ${
                    h.level === 3 ? "pl-4 text-[11px]" : "font-medium"
                  } ${
                    isActive
                      ? "bg-[#EDF7FF] text-[#0B7CF2] font-bold translate-x-1"
                      : "text-[#536B89] hover:text-[#071E61] hover:bg-slate-50"
                  }`}
                >
                  {h.text}
                </a>
              );
            })}
          </nav>
        </div>
      )}

      {/* 2. Top Ranking Articles Panel */}
      <div className="bg-white rounded-2xl border border-[#082E67]/10 p-6 shadow-[0_8px_24px_rgba(8,42,102,0.05)]">
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-[#EAF5FF] text-[#0B7CF2] flex items-center justify-center">
              <TrendingUp className="w-4 h-4" />
            </span>
            <h3 className="font-heading font-black text-[15px] text-[#071E61]">
              Top Ranking Articles
            </h3>
          </div>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-[#536B89]">
            Editorial
          </span>
        </div>

        <div className="divide-y divide-slate-100">
          {topRankingArticles.slice(0, 3).map((article, idx) => (
            <Link
              key={article.id}
              to={`/blog/${article.slug}`}
              className="group py-3.5 first:pt-0 last:pb-0 flex items-start gap-3 transition-colors block"
            >
              <span className="font-heading font-black text-xs text-[#0B7CF2]/50 group-hover:text-[#0B7CF2] transition-colors shrink-0 w-5 pt-0.5">
                {String(idx + 1).padStart(2, "0")}
              </span>

              <div className="min-w-0">
                <span className="text-[9px] font-bold tracking-wider uppercase text-[#0B7CF2] block mb-0.5">
                  {article.category}
                </span>
                <h4 className="text-[13px] font-bold text-[#071E61] group-hover:text-[#0B7CF2] transition-colors leading-snug line-clamp-2">
                  {article.title}
                </h4>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-[#6A7D97]">
                  <span>{article.readTime}</span>
                  <span>•</span>
                  <span className="text-[#0B7CF2] font-medium">Top read</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* 3. Dark Green Systems Audit CTA Card */}
      <div className="rounded-2xl p-6 bg-gradient-to-br from-[#06313B] to-[#0A4854] text-white shadow-[0_16px_36px_rgba(6,49,59,0.22)] relative overflow-hidden border border-white/10">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-[#48F45A] text-[10px] font-bold tracking-wider uppercase mb-3 border border-white/10">
          <Sparkles className="w-3 h-3" />
          <span>FREE • 15 MIN</span>
        </div>

        <h3 className="text-xl font-heading font-extrabold text-white leading-tight mb-2">
          You're losing 20+ hours a week
        </h3>
        <p className="text-xs text-white/80 leading-relaxed mb-4">
          Get a tailored delegation audit and we'll pinpoint the exact tasks you
          can hand off today.
        </p>

        {/* 3 Metric Pills */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
          <div className="bg-white/10 rounded-lg p-2 border border-white/10">
            <span className="block text-base font-black text-white">20+</span>
            <span className="text-[9px] uppercase tracking-wider text-white/70">
              HRS SAVED/WK
            </span>
          </div>
          <div className="bg-white/10 rounded-lg p-2 border border-white/10">
            <span className="block text-base font-black text-white">15</span>
            <span className="text-[9px] uppercase tracking-wider text-white/70">
              MIN CALL
            </span>
          </div>
          <div className="bg-white/10 rounded-lg p-2 border border-white/10">
            <span className="block text-base font-black text-white">100%</span>
            <span className="text-[9px] uppercase tracking-wider text-white/70">
              FREE
            </span>
          </div>
        </div>

        {/* Action Form */}
        <form onSubmit={handleAuditSubmit} className="space-y-2">
          <input
            type="email"
            placeholder="Enter work email..."
            value={auditEmail}
            onChange={(e) => setAuditEmail(e.target.value)}
            className="w-full h-10 px-3.5 bg-white/15 border border-white/20 rounded-lg text-xs text-white placeholder:text-white/60 focus:outline-none focus:bg-white/25 transition-all"
            required
          />
          <button
            type="submit"
            className="w-full h-11 bg-white hover:bg-slate-100 text-[#06313B] font-bold text-xs rounded-lg shadow-md transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <span>Get My Systems Audit</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </form>
        <p className="text-[10px] text-white/60 text-center mt-2.5">
          No card required • Joined by 300+ agency founders
        </p>
      </div>

      {/* 4. Free Strategy Call Card (Teal/Emerald Gradient) */}
      <div className="rounded-2xl p-6 bg-gradient-to-br from-[#0B5145] to-[#147464] text-white shadow-[0_16px_36px_rgba(11,81,69,0.22)] relative overflow-hidden border border-white/10">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#FFB800] text-[#071E61] text-[10px] font-black uppercase tracking-wider mb-3">
          <Flame className="w-3 h-3 fill-current" />
          <span>FREE CONSULTATION</span>
        </div>

        <h3 className="text-lg font-heading font-black text-white leading-tight mb-2">
          Talk to an Expert Systems Architect
        </h3>

        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-3xl font-heading font-black text-[#48F45A]">
            FREE
          </span>
          <span className="text-[11px] text-white/80 font-medium">
            instead of costly consulting fees
          </span>
        </div>

        <p className="text-xs text-white/85 leading-relaxed mb-4">
          Audit your revenue workflows and sub-account architecture 1-on-1 —
          100% free with zero pitch.
        </p>

        <ul className="space-y-2 mb-5 text-xs text-white/90">
          <li className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#48F45A] shrink-0" />
            <span>Direct diagnostic of your current tech stack</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#48F45A] shrink-0" />
            <span>Actionable blueprint you keep and deploy</span>
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#48F45A] shrink-0" />
            <span>Zero pitch, zero commitment required</span>
          </li>
        </ul>

        <Link
          to="/book"
          className="w-full h-11 bg-[#FFD600] hover:bg-[#FFBF00] text-[#071E61] font-black text-xs rounded-lg shadow-md transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-1.5"
        >
          <span>Book Free Strategy Call</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </aside>
  );
}
